import ApiService from './api';

/**
 * Admin Service
 * Handles all admin-related API requests
 */
const AdminService = {
  /**
   * Get all users with pagination and filtering
   * @param {Object} queryParams - Query parameters for filtering
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  getUsers: async (queryParams, token) => {
    const queryString = new URLSearchParams(queryParams).toString();
    const response = await ApiService.get(`/api/admin/users?${queryString}`, {
      'Authorization': `Bearer ${token}`
    });
    
    return response.json();
  },

  /**
   * Get user details by ID
   * @param {string} userId - User ID
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  getUserById: async (userId, token) => {
    const response = await ApiService.get(`/api/admin/users/${userId}`, {
      'Authorization': `Bearer ${token}`
    });
    
    return response.json();
  },

  /**
   * Update user role
   * @param {string} userId - User ID
   * @param {string} role - New role
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  updateUserRole: async (userId, role, token) => {
    const response = await ApiService.put(`/api/admin/users/${userId}/role`, { role }, {
      'Authorization': `Bearer ${token}`
    });
    
    return response.json();
  },

  /**
   * Update user status (active/inactive)
   * @param {string} userId - User ID
   * @param {boolean} isActive - User status
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  updateUserStatus: async (userId, isActive, token) => {
    const response = await ApiService.put(`/api/admin/users/${userId}/status`, { isActive }, {
      'Authorization': `Bearer ${token}`
    });
    
    return response.json();
  },

  /**
   * Get statistics overview
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  getStatsOverview: async (token) => {
    const response = await ApiService.get('/api/admin/stats', {
      'Authorization': `Bearer ${token}`
    });
    
    return response.json();
  },

  /**
   * Get user statistics
   * @param {string} timeframe - Timeframe for statistics
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  getUserStats: async (timeframe, token) => {
    const response = await ApiService.get(`/api/admin/stats/users?timeframe=${timeframe}`, {
      'Authorization': `Bearer ${token}`
    });
    
    return response.json();
  },

  /**
   * Get workout statistics
   * @param {string} timeframe - Timeframe for statistics
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  getWorkoutStats: async (timeframe, token) => {
    const response = await ApiService.get(`/api/admin/stats/workouts?timeframe=${timeframe}`, {
      'Authorization': `Bearer ${token}`
    });
    
    return response.json();
  },

  /**
   * Get exercise category statistics
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  getCategoryStats: async (token) => {
    const response = await ApiService.get('/api/admin/stats/categories', {
      'Authorization': `Bearer ${token}`
    });
    
    return response.json();
  },

  /**
   * Get sub-exercises for an exercise
   * @param {string} exerciseId - Exercise ID
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  getSubExercises: async (exerciseId, token) => {
    const response = await ApiService.get(`/api/admin/exercises/${exerciseId}/sub-exercises`, {
      'Authorization': `Bearer ${token}`
    });
    
    return response.json();
  },

  /**
   * Delete a sub-exercise
   * @param {string} subExerciseId - Sub-exercise ID
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  deleteSubExercise: async (subExerciseId, token) => {
    const response = await ApiService.delete(`/api/admin/sub-exercises/${subExerciseId}`, {
      'Authorization': `Bearer ${token}`
    });
    
    return response.json();
  },

  /**
   * Reorder sub-exercises
   * @param {string} exerciseId - Exercise ID
   * @param {Array} orderData - Order data with sub-exercise IDs and positions
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  reorderSubExercises: async (exerciseId, orderData, token) => {
    const response = await ApiService.put(`/api/admin/exercises/${exerciseId}/sub-exercises/reorder`, orderData, {
      'Authorization': `Bearer ${token}`
    });
    
    return response.json();
  }
};

export default AdminService;
